import {
    Component,
    OnInit,
    Input,
    ViewChild,
    ElementRef,
    Renderer2,
    Output,
    EventEmitter
} from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    UntypedFormControl,
    Validators
} from '@angular/forms'; // Used for Reactive Forms
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
//import { NgbPopoverConfig } from '@ng-bootstrap/ng-bootstrap';
import { Clipboard } from '@angular/cdk/clipboard';
import { TooltipPosition } from '@angular/material/tooltip';

import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { DataCollectionMilestone } from '../../../shared/models/milestones/data-collection-milestone.model';
import { MilestoneType } from '../../../shared/models/milestones/milestone-enums';
import { NotificationService } from '../../../shared/services/notification.service';
import { NotificationInstance } from '../../../shared/models/notifications/notification-instance.model';
import { DateUtil } from '../../../shared/utility-classes/date.util';
import { SiteWorkflowState } from '../../../shared/enums/site-workflow-state.enum';
import { SiteMilestoneName } from '../../../shared/models/milestones/milestone-enums';
import { Milestone } from '../../../shared/models/milestones/milestone.model';
import { DisplayTimeAndHoursOffset } from '../../../shared/models/misc-metadata/display-time-and-hours-offset.model';
import { MilestoneService } from '../../../shared/services/milestone.service';
import { MiscellaneousMetadataService } from '../../../shared/services/miscellaneous-metadata.service';

@Component({
    selector: 'app-site-milestone-config',
    templateUrl: './site-milestone-config.component.html',
    styleUrls: ['./site-milestone-config.component.scss'],
    standalone: false
})
export class SiteMilestoneConfigComponent implements OnInit {
    // Input properties.
    @Input() site: DataCollection;
    @Input() milestone: DataCollectionMilestone;
    @Input() availableMilestoneNames: string[] = null;
    @Input() schedulerAvailable: boolean = false;

    // Outputs.
    @Output() milestoneChanged: EventEmitter<DataCollectionMilestone> = new EventEmitter();

    // View children.
    @ViewChild(NgbPopover, { read: NgbPopover }) notificationPreviewPopover: NgbPopover;
    //@ViewChild(NgbPopover, { read: ElementRef }) notificationPreviewPopoverElementRef: ElementRef;

    // Additional properties.
    private formGroup: UntypedFormGroup = null;
    private configuredMilestone: DataCollectionMilestone = null;
    private generatingNotficationPreview: boolean = false;
    private notificationPreview: NotificationInstance = null;
    private notificationCopiedToClipboard: boolean = false;
    private targetWorkflowState: string = SiteWorkflowState.Active;

    // Read only data.
    // Note:  during initial development, the following
    //        options are not being retrieved from the server.
    private readonly allSiteEventNames: string[] = [SiteMilestoneName.SiteOpening, SiteMilestoneName.SiteClosing];
    private siteEventNames: string[] = null;

    private targetDisplayTimes: DisplayTimeAndHoursOffset[] = null;

    // Constructor.
    public constructor(private _formBuilder: UntypedFormBuilder,
        private renderer: Renderer2,
        private clipboard: Clipboard,
        private milestoneService: MilestoneService,
        private notificationService: NotificationService,
        private miscellaneousMetadataService: MiscellaneousMetadataService) {
        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.siteEventNames = (this.availableMilestoneNames != null ? this.availableMilestoneNames : this.siteEventNames);
        //this.setupFormControls();
        this.getDisplayTimes();
    }

    // Accessor methods.
    // Form controls.
    public get FormGroup(): UntypedFormGroup {
        return this.formGroup;
    }
    public get EventTypeFormControl(): UntypedFormControl {
        let formControl: UntypedFormControl = <UntypedFormControl>this.formGroup.get('eventType');

        return formControl;
    }
    public get EventType(): string {
        let eventType: string = this.SiteEventNames[this.EventTypeFormControl.value];

        return eventType;
    }
    public get TargetDateFormControl(): UntypedFormControl {
        let formControl: UntypedFormControl = <UntypedFormControl>this.formGroup.get('targetDate');

        return formControl;
    }
    public get TargetTimeFormControl(): UntypedFormControl {
        let formControl: UntypedFormControl = <UntypedFormControl>this.formGroup.get('targetTime');

        return formControl;
    }
    /*
    public get TargetWorkflowStateFormControl(): FormControl {
        let formControl: FormControl = <FormControl>this.formGroup.get('targetWorkflowState');

        return formControl;
    }
    */
    public get AutoSetWorkflowStateFormControl(): UntypedFormControl {
        let formControl: UntypedFormControl = <UntypedFormControl>this.formGroup.get('autoSetWorkflowState');

        return formControl;
    }
    public get SendNotificationFormControl(): UntypedFormControl {
        let formControl: UntypedFormControl = <UntypedFormControl>this.formGroup.get('sendNotificationFlag');

        return formControl;
    }
    public get NotificationTemplateFormControl(): UntypedFormControl {
        let formControl: UntypedFormControl = <UntypedFormControl>this.formGroup.get('notificationTemplate');

        return formControl;
    }

    // Control data.
    public get SiteEventNames(): string[] {
        return this.siteEventNames;
    }
    public get SiteEventDescription(): string {
        return (this.configuredMilestone != null ? this.configuredMilestone.Description : 'Undefined');
    }

    public get AutoSetWorkflowStateTooltip(): string {
        return "When this is checked, the site's workflow state will get set automatically on the target date and time";
    }
    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }

    public get MinMilestoneDate(): Date {
        let tomorrow: Date = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        return tomorrow;
    }
    public get MaxMilestoneDate(): Date {
        let today: Date = new Date();
        let tomorrowPlusOneYear: Date = new Date();
        tomorrowPlusOneYear.setDate(today.getDate() + 365 + (today.getFullYear() % 4 == 0 ? 1 : 0)); // Leap year.

        return tomorrowPlusOneYear;
    }
    public get MilestoneTimes(): DisplayTimeAndHoursOffset[] {
        //return this.targetTimes;
        return this.targetDisplayTimes;
    }

    public get NotificationPreviewStatus(): string {
        let status: string = null;

        if (this.generatingNotficationPreview)
            status = 'Generating ...';
        else if (this.notificationCopiedToClipboard)
            status = 'Copied to clipboard';

        return status;
    }

    public get CopyPreviewToClipboardButtonDisabled(): boolean {
        return (this.notificationPreview == null);
    }

    public get NotificationPreviewTitle(): string {
        return 'Notification Preview';
    }

    public get NotificationPreviewContent(): string {
        return (this.notificationPreview != null ? this.notificationPreview.notificationText : null);
    }

    // Control event handler methods.
    public notificationPreviewClicked(): void {
        let milestone: DataCollectionMilestone = this.packageConfiguredMilestone();

        this.notificationPreview = null;
        this.generatingNotficationPreview = true;
        this.notificationCopiedToClipboard = false;
        this.notificationService.getSiteMilestoneTemplatePreview(milestone).then(response => {
            this.notificationPreview = response;
            this.generatingNotficationPreview = false;

            // Open the permissions preview popover.
            if (this.notificationPreviewPopover != null) {
                this.notificationPreviewPopover.open();
            }                
        });

        return;
    }

    public copyNotificationPreviewToClipboard(): void {
        let content: string = this.notificationPreview.notificationText;
        this.clipboard.copy(content);
        this.notificationCopiedToClipboard = true;
    }

    // Private helper methods.
    private setupFormControls(): void {
        let eventTypeValue: number = 0;
        let dateValue: string = DateUtil.TodayToUTCDateString();
        //let timeValue: number = this.targetTimes[0].hoursOffset;
        let timeValue: number = this.targetDisplayTimes[0].hoursOffset;
        let autosetWorkflowState: boolean = true;
        let sendNotificationValue: string = 'yes';

        // If we are editing an existing milestone,
        // apply existing values to the form controls.
        if ((this.milestone != null) && (this.milestone.id > 0)) {
            dateValue = DateUtil.DateToUTCDateString(this.milestone.targetDate.toString());

            let displayTimeAndHours: DisplayTimeAndHoursOffset =
                //this.targetTimes.find(t => t.hoursOffset == this.milestone.hoursOffset);
                this.targetDisplayTimes.find(t => t.hoursOffset == this.milestone.hoursOffset);
            if (displayTimeAndHours != null)
                timeValue = displayTimeAndHours.hoursOffset;

            autosetWorkflowState = this.milestone.automaticallyPerformAction;

            sendNotificationValue = (this.milestone.sendNotification ? 'yes' : 'no');
        }

        // Start with default form control values.
        this.formGroup = this._formBuilder.group({
            eventType: [eventTypeValue, Validators.required], // e.g. 'Site Opening', 'Site Closing'

            targetDate: [dateValue, Validators.required],    // The milestone's target date
            targetTime: [timeValue, Validators.required],    // The milestone's target date

            autoSetWorkflowState: [autosetWorkflowState, Validators.required], // Whether to automatically set the workflow state (default's to true)

            sendNotificationFlag: [sendNotificationValue, Validators.required], // Whether or not to send a notification
            // Note:  notification template selection not yet enabled.
        });

        // Set the target workflow state to the event name.
        this.setTargetWorkflowState(this.SiteEventNames[0]);

        // Output the initial milestone configuration.
        this.outputMilestone();

        // Listen to form value changes.
        this.formGroup.valueChanges.subscribe(val => {            
            this.outputMilestone();
        });

        // Listen to value changes (all controls for now).
        let eventTypeFormControl: UntypedFormControl = this.EventTypeFormControl;
        eventTypeFormControl.valueChanges
            .subscribe(val => {
                console.log('Event type changed.');
                /*
                if (val == 'Site Opening') {
                    this.targetWorkflowState = SiteWorkflowState.Active;
                } else if (val == 'Site Closing') {
                    this.targetWorkflowState = SiteWorkflowState.Closed;
                }
                */
                let eventName: string = this.allSiteEventNames[val];
                this.setTargetWorkflowState(eventName);
            });

        let targetDateFormControl: UntypedFormControl = this.TargetDateFormControl;
        targetDateFormControl.valueChanges
            .subscribe(dateValue => {
                let targetDateFC: UntypedFormControl = this.TargetDateFormControl;

                console.log('Date changed.');
            });

        let targetTimeFormControl: UntypedFormControl = this.TargetTimeFormControl;
        targetTimeFormControl.valueChanges
            .subscribe(timeValue => {
                let targetTimeFC: UntypedFormControl = this.TargetTimeFormControl;

                console.log('Time changed.');
            });

        /*
        let targetWorkflowState: FormControl = this.TargetWorkflowStateFormControl;
        targetWorkflowState.valueChanges
            .subscribe(workflowState => {
                console.log('Workflow state changed.');
            });
        */

        let autoSetWorkflowState: UntypedFormControl = this.AutoSetWorkflowStateFormControl;
        autoSetWorkflowState.valueChanges
            .subscribe(workflowState => {
                console.log('Auto-set workflow state changed.');
            });

        let sendNotificationFlag: UntypedFormControl = this.SendNotificationFormControl;
        sendNotificationFlag.valueChanges
            .subscribe(flagValue => {
                console.log('Auto-set workflow state changed.');
            });
    }

    private setTargetWorkflowState(siteEventName: string): void {
        if (siteEventName == 'Site Opening') {
            this.targetWorkflowState = SiteWorkflowState.Active;
        } else if (siteEventName == 'Site Closing') {
            this.targetWorkflowState = SiteWorkflowState.Closed;
        }
    }

    private packageConfiguredMilestone(): DataCollectionMilestone {
        let eventType: string = this.EventType;

        let milestone: DataCollectionMilestone = new DataCollectionMilestone();
            
        milestone.id = this.milestone.id;

        milestone.dataCollectionId = this.site.id;

        milestone.name = eventType;
        milestone.description = `${eventType} Milestone`;
        milestone.milestoneType = MilestoneType.Site;

        milestone.action = Milestone.Action_SetWorkflowState;
        milestone.targetWorkflowState = this.targetWorkflowState; //this.TargetWorkflowStateFormControl.value;
        milestone.automaticallyPerformAction = this.AutoSetWorkflowStateFormControl.value;

        //milestone.targetDate = this.TargetDateFormControl.value;
        milestone.targetDate = DateUtil.DateToUTCDate(this.TargetDateFormControl.value, 0, this.TargetTimeFormControl.value);
        //milestone.targetTimeDisplayValue = this.TargetTimeFormControl.value;
        milestone.hoursOffset = this.TargetTimeFormControl.value;

        milestone.sendNotification = (this.SendNotificationFormControl.value == 'yes' ? true : false);
        milestone.notificationTemplateName = eventType; // Same as eventType for now.

        return milestone;
    }

    private outputMilestone(): void {
        //let milestone: DataCollectionMilestone = this.packageConfiguredMilestone();
        this.configuredMilestone = this.packageConfiguredMilestone();
        //this.milestoneChanged.emit(milestone);
        this.milestoneChanged.emit(this.configuredMilestone);
    }

    private getDisplayTimes(): void {
        this.miscellaneousMetadataService.getDisplayTimes().then(response => {
            this.targetDisplayTimes = response;

            this.setupFormControls();
        });
    }
}
